import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ZoomIn, X, Newspaper } from "lucide-react";

export default function Article() {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [zoom, setZoom] = useState(false);

    useEffect(() => {
        api.get(`/blog/${slug}`)
            .then((r) => setP(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound)
        return (
            <div className="container-hero py-32">
                <h1 className="font-display text-4xl">Post not found</h1>
                <Link to="/media" className="text-brand-forest mt-4 inline-block">
                    ← Back to media
                </Link>
            </div>
        );
    if (!p) return <div className="container-hero py-32 text-slate-500">Loading…</div>;

    const isPress = (p.category || "").toLowerCase().includes("press");

    return (
        <article data-testid="article-page" className="pb-24">
            <section className="pt-16 md:pt-24 pb-8">
                <div className="container-hero max-w-3xl">
                    <Link
                        to="/media"
                        className="inline-flex items-center gap-2 text-sm text-brand-forest font-semibold mb-8"
                    >
                        <ArrowLeft size={16} /> All stories
                    </Link>
                    <div className="label-eyebrow text-brand-forest mb-4 inline-flex items-center gap-2">
                        {isPress && <Newspaper size={14} strokeWidth={1.75} />} {p.category}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.05]">
                        {p.title}
                    </h1>
                    <div className="text-sm text-slate-500 mt-4">
                        {p.created_at ? new Date(p.created_at).toDateString() : ""}
                    </div>
                </div>
            </section>

            {p.cover_image && (
                <div className="container-hero max-w-5xl mb-10">
                    <button
                        onClick={() => setZoom(true)}
                        data-testid="article-cover-zoom"
                        className="group relative block w-full overflow-hidden rounded-2xl bg-brand-stone focus:outline-none focus:ring-2 focus:ring-brand-forest"
                    >
                        <img
                            src={p.cover_image}
                            alt={p.title}
                            className="w-full max-h-[75vh] object-contain bg-white"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-4 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent text-white text-sm">
                            <span className="inline-flex items-center gap-2">
                                <ZoomIn size={16} strokeWidth={1.75} />
                                {isPress ? "Click to read full newspaper cutting" : "Click to view full image"}
                            </span>
                        </div>
                    </button>
                </div>
            )}

            <div className="container-hero max-w-3xl">
                <div className="text-lg leading-relaxed text-slate-700 whitespace-pre-line">
                    {p.body}
                </div>
            </div>

            {/* Zoom lightbox */}
            {zoom && p.cover_image && (
                <div
                    data-testid="article-lightbox"
                    onClick={() => setZoom(false)}
                    className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 overflow-auto"
                >
                    <button
                        onClick={() => setZoom(false)}
                        aria-label="Close"
                        className="fixed top-6 right-6 z-10 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                    >
                        <X />
                    </button>
                    <img
                        src={p.cover_image}
                        alt={p.title}
                        onClick={(e) => e.stopPropagation()}
                        className="max-w-none w-auto h-auto cursor-zoom-out shadow-2xl rounded"
                        style={{ maxHeight: "none" }}
                    />
                </div>
            )}
        </article>
    );
}
